import React, { useState, useEffect, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import CropSquareIcon from '@material-ui/icons/CropSquare';
import RemoveIcon from '@material-ui/icons/Remove';
import { Resizable } from 're-resizable';
import { useDispatch, useSelector } from 'react-redux';

import './style.css';
import BottomDrawerContent from './BottomDrawerContent';
import { BottomDrawerProvider } from '../../../../context/BottomDrawer/BottomDrawerProvider';
import { useBottomDrawer } from '../../../../hooks/useBottomDrawer';
import { getDetailsFromUrl } from '../../../../services/blueprint';
import { invokePostService, setIsDocumentClicked, setActiveLabel, setForcedFocus } from '../../../../store/global/actions';
import { getCodeEditStatus } from '../../../../store/global/selectors';

const LOCAL_STORAGE_HEIGHT = "BOTTOM_DRAWER_HEIGHT";
const LOCAL_STORAGE_WIDTH = "BOTTOM_DRAWER_WIDTH";
const LOCAL_STORAGE_DOCK = "BOTTOM_DRAWER_DOCK";

const SIDEBAR_BUTTON_LAYOUT = 64;
const SIDEBAR_LIST_WIDTH = 171;
const TOOLBAR_HEIGHT = 40;
const MAX_DRAWER_HEIGHT = window.innerHeight - ((20 * window.innerHeight)/100);
const DEFAULT_DRAWER_HEIGHT = window.innerHeight * 0.5;
const DEFAULT_DRAWER_WIDTH = `calc(${document.documentElement.clientWidth}px - ${SIDEBAR_BUTTON_LAYOUT}px - ${SIDEBAR_LIST_WIDTH}px)`;
const DEFAULT_RIGHT_DRAWER_WIDTH = 1025;
const TOPBAR_HEIGHT = 64;
const DEFAULT_RIGHT_DRAWER_HEIGHT = window.innerHeight - 64;
const MIN_DRAWER_WIDTH = 875;

const useStyles = makeStyles((theme) => ({
    drawer: ({dockPosition}) => ({
        position: 'fixed !important',
        bottom: dockPosition === 'bottom' ? 0 : 'auto',
        top: dockPosition === "right" ? TOPBAR_HEIGHT : 'auto',
        right: 0,
        width: dockPosition === 'right' ? DEFAULT_RIGHT_DRAWER_WIDTH : DEFAULT_DRAWER_WIDTH,
        zIndex: 1300,
        display: 'flex',
        flexDirection: 'column',
        color: '#000',
        boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)',
        overflow: 'visible',
    }),
    toolbar: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        paddingRight: theme.spacing(1),
    },
    drawerHeader: {
        display: 'flex',
        justifyContent: "space-between",
        alignItems: 'center',
        background: '#3c4756',
        padding: '3px 0',
    },
    iconButton: {
        width: 22,
        height: 22,
        margin: '4px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center !important',
        justifyContent: 'center !important',
        color: '#ffffff',
        '& svg': {
            fontSize: '20px',
        },
        '&:hover': {
            backgroundColor: '#ffffff17',
        },
        '&:focus': {
            outline: 'none',
            boxShadow: 'none',
        },
    },
    iconButtonMinimised: {
        width: 30,
        height: 30,
        margin: '4px',
        borderRadius: '50%',
        color: 'grey',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '& svg': {
            fontSize: '20px',
        },
        '&:hover': {
            backgroundColor: 'grey',
            color: '#fff'
        },
        '&:focus': {
            outline: 'none',
            boxShadow: 'none',
        },
    },
    content: {
        flex: 1,
        padding: 0,
        overflowY: 'auto',
        backgroundColor: '#ffffff',
        margin: 0,
        '& .MuiGrid-item': {
            padding: '0 !important',
          },
    },
    drawerTitle: {
        fontSize: "1rem",
        fontWeight: 500,
        color: "#ffffff",
        marginBottom: 0,
        textAlign: "center",
        "&::first-letter": {
          textTransform: "uppercase",
        },
      }
}));

function BottomDrawer({isOpen, onClose, title, children, leftPane, screen, onTabChange, showTour}) {
    const {dockPosition, setActiveTab} = useBottomDrawer()
    const classes = useStyles({dockPosition});
    const dispatch = useDispatch();
    const urlDetails = getDetailsFromUrl();
    const codeEditStatus = useSelector(getCodeEditStatus);

    const [availableWidth, setAvailableWidth] = useState(0);
    const [height, setHeight] = useState(DEFAULT_DRAWER_HEIGHT);
    const [width, setWidth] = useState(DEFAULT_RIGHT_DRAWER_WIDTH)
    const [minimized, setMinimized] = useState(false);
    const [open, setOpen] = useState(false)
    const [interactiveWidth, setInteractiveWidth] = useState(DEFAULT_RIGHT_DRAWER_WIDTH)

    const adjustScreenHeight = ({drawerHeight=0}) => {
        const canvas = document.getElementById("transaction_container");
        if (canvas && open) {
            canvas.style.height = `calc(100vh - ${TOPBAR_HEIGHT}px - ${drawerHeight}px)`;
        }
        if(open){
            switch(screen){
                case "view":
                    const mainDiv = document.querySelector("#main-content-div");
                    mainDiv.style.paddingBottom = `${drawerHeight}px`;
                    // console.log({screen, drawerHeight})
                    break;
                default:
                    break;
            }
        }
    }

    useEffect(() => {
        const mainDiv = document.querySelector('#main-content-div');
        const resizeObserver = new ResizeObserver(() => {
            setAvailableWidth(mainDiv.offsetWidth)
        });

        resizeObserver.observe(mainDiv)

        return () => resizeObserver.disconnect()
    },[])

    useMemo(() => {
        setOpen(isOpen)
        if(!isOpen){
            adjustScreenHeight({})
        }
    }, [isOpen])

    useEffect(() => {
        if (height <= TOOLBAR_HEIGHT * 3) {
            setMinimized(true);
            adjustScreenHeight({drawerHeight: TOOLBAR_HEIGHT});
        } else {
            setMinimized(false);
        }
        
    }, [height]);

    useMemo(() => {
        if(dockPosition === 'right'){
            setHeight(DEFAULT_RIGHT_DRAWER_HEIGHT)

            const storedWidth = parseInt(localStorage.getItem(LOCAL_STORAGE_WIDTH)) || DEFAULT_RIGHT_DRAWER_WIDTH;
            // Constrain the width to availableWidth
            const newWidth = Math.min(storedWidth, availableWidth);
            setWidth(newWidth);
            setInteractiveWidth(newWidth);
            adjustScreenHeight({ drawerHeight: 0 });

            // setWidth(parseInt(localStorage.getItem(LOCAL_STORAGE_WIDTH)) || DEFAULT_RIGHT_DRAWER_WIDTH)
            // adjustScreenHeight({drawerHeight: 0});
        }else if(dockPosition === 'bottom'){
            const newHeight = parseInt(localStorage.getItem(LOCAL_STORAGE_HEIGHT)) || DEFAULT_DRAWER_HEIGHT;
            setHeight(newHeight)
            adjustScreenHeight({drawerHeight: newHeight});
            setWidth(availableWidth)
        }
        localStorage.setItem(LOCAL_STORAGE_DOCK, dockPosition)
    },[open, dockPosition, availableWidth])

    useMemo(() => {
        if(dockPosition === "bottom"){
            localStorage.setItem(LOCAL_STORAGE_HEIGHT, height)
        }else{
            localStorage.setItem(LOCAL_STORAGE_WIDTH, width)
            setInteractiveWidth(width)
        }
    },[width, height, dockPosition])

    const handleResize = (e, direction, ref, d) => {
        if(dockPosition === 'bottom'){
            const newHeight = ref.offsetHeight;
            adjustScreenHeight({drawerHeight: newHeight});
        }else if(dockPosition === 'right'){
            setInteractiveWidth(ref.offsetWidth)
        }
    };

    const handleOnClose = () => {
        if (codeEditStatus) {
            dispatch(setIsDocumentClicked());
            return;
        }

        setOpen(false);
        dispatch(setActiveLabel(null))
        setActiveTab(0);
        dispatch(setForcedFocus(false));
        adjustScreenHeight({drawerHeight: 0});

        if(onClose && typeof onClose === "function"){
            onClose();
        }

        if(minimized){
            localStorage.setItem(LOCAL_STORAGE_HEIGHT, DEFAULT_DRAWER_HEIGHT)
            setMinimized(false);
        }
        let typesNotIn = urlDetails?.screen === 'transaction' ? 'transaction' : urlDetails?.screen === 'view' ? 'view' : [];
        dispatch(invokePostService('removeLocks', { typesNotIn }, []));
    };

    const handleResizeStop = (e, direction, ref, d) => {
        if(dockPosition === 'bottom'){
            const newHeight = ref.offsetHeight;
            setHeight(Math.min(newHeight, MAX_DRAWER_HEIGHT));
        }else if(dockPosition === 'right'){
            const newWidth = ref.offsetWidth;
            setWidth(Math.min(newWidth, availableWidth))
        }
    }

    const handleExpandAndMinimise = () => {
         if (codeEditStatus) {
            dispatch(setIsDocumentClicked());
            return;
        }

        if (minimized) {
            const defaultHeight = localStorage.getItem(LOCAL_STORAGE_HEIGHT) || DEFAULT_DRAWER_HEIGHT;
            setHeight(defaultHeight);
            setMinimized(false);
            adjustScreenHeight({drawerHeight: defaultHeight});
        } else {
            setMinimized(true);
            adjustScreenHeight({drawerHeight: TOOLBAR_HEIGHT});
            // localStorage.setItem(LOCAL_STORAGE_HEIGHT, TOOLBAR_HEIGHT)
        }
    }



    return (
        <>
            {
                open &&
                <Resizable
                    id={"bottom_drawer_container"}
                    size={{
                        width,
                        height: minimized ? TOOLBAR_HEIGHT : height
                    }}
                    enable={{
                        top: !minimized && dockPosition === 'bottom',
                        right: false,
                        bottom: false,
                        left: dockPosition === 'right',
                        topRight: false,
                        topLeft: false,
                        bottomRight: false,
                        bottomLeft: false,
                    }}
                    minHeight={TOOLBAR_HEIGHT}
                    maxHeight={dockPosition === 'bottom' ? MAX_DRAWER_HEIGHT : 5000}
                    maxWidth={dockPosition === "bottom" ? availableWidth: (availableWidth - ((availableWidth * 10)/100))}
                    minWidth={MIN_DRAWER_WIDTH}
                    onResize={handleResize}
                    onResizeStop={handleResizeStop}
                    className={`${classes.drawer} ${dockPosition === 'bottom' ? 'drawer-dock-bottom' : 'drawer-dock-right'}`}
                    handleComponent={{
                        top: (
                          <div className="custom_drag_handle" title={"Resize drawer"}>
                            <span className="drag_lines"></span>
                          </div>
                        ),
                        left: (
                            <div className="custom_drag_handle handle-left" title={"Resize drawer"}>
                                <span className="drag_lines"></span>
                            </div>
                        )
                      }}
                >
                    <header className={classes.drawerHeader} id="drawer-header">
                        <div style={{flex: 1, justifyContent: 'center'}}>
                            <h2 
                                className={classes.drawerTitle}

                            >{title}</h2>
                        </div>
                        <div className={`${classes.toolbar} ${minimized ? classes.toolbarMinimised : ''}`}>
                            {
                                dockPosition === 'bottom' &&
                                <IconButton
                                    size="small"
                                    className={minimized ? classes.iconButtonMinimised : classes.iconButton}
                                    onClick={() => { handleExpandAndMinimise() }}
                                    id="minimize-button"
                                >
                                    {minimized ? <CropSquareIcon /> : <RemoveIcon style={{ transform: 'translateY(1px)' }} />}
                                </IconButton>
                            }
                            <IconButton
                                size="small"
                                className={minimized ? classes.iconButtonMinimised : classes.iconButton}
                                id="close-button"
                                onClick={handleOnClose}
                            >
                                <CloseIcon />
                            </IconButton>
                        </div>
                    </header>
                    {!minimized && (
                        <div className={`${classes.content} bottom_drawer_content`}>
                            <BottomDrawerContent leftPane={leftPane} onTabChange={onTabChange} drawerWidth={width} currentWidth={interactiveWidth} showTour={showTour}>
                                {children}
                            </BottomDrawerContent>
                        </div>
                    )}
                </Resizable>
            }
        </>
    );
}


export default function BottomDrawerWrapper(props) {
    return(
        <BottomDrawerProvider>
            <BottomDrawer {...props}/>
        </BottomDrawerProvider>
    )
}